import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, mergeMap, Observable } from 'rxjs';
import { CodedValueItem } from '../interfaces/coded-value-item.interface';
import {
  EEstadoEntidadeNotificada,
  OccurrenceAttachmentModelLike,
  OccurrenceModel,
  OccurrenceModelLike,
  OccurrenceType,
  OccurrenceTypeModelLike,
  StatusOccurrenceLike,
  StatusOccurrenceType,
} from '../models/occurrence.model';
import { PaginationResult } from '../models/pagination-result.model';
import { CacheFactoryService, ICacheService } from './cache-factory.service';
import moment from 'moment';

interface OccurrenceDTOLike {
  id?: string;
  idUtilizadorCriacao?: string;
  idEntidade?: string;
  idEntidadeResponsavel?: string;
  entidadesNotificadas?: {
    idEntidade?: string;
    estado?: EEstadoEntidadeNotificada;
  }[];
  dataOcorrencia?: Date;
  idsic?: string;
  statusOcorrenciaId?: string;
  statusOcorrencia?: { id: string; idSiia: string };
  tipoOcorrenciaId: string;
  tipoOcorrencia?: { id: string; idSiia?: OccurrenceType };
  observacoes: string;
  notas: {
    id?: string;
    nota: string;
  }[];
  anexos: {
    id?: string;
    nome: string;
    url: string;
  }[];
  objetos: {
    id?: string;
    tipoObjeto: string;
    idObjetoCadastro: string;
    idEntidade: string;
  }[];
  geometry: string;
}

export interface SearchOccurrenceFilterLike {
  status: string;
  idSiia: string;
  type: string;
  entityId: string;
  entityIdNotified: string;
  startDate: Date;
  endDate: Date;
}

@Injectable()
export class OccurrenceService {
  private cacheService: ICacheService;

  constructor(private http: HttpClient, cacheFactory: CacheFactoryService) {
    this.cacheService = cacheFactory.getOrCreate('occurrences');
  }

  getOccurrenceStatusList() {
    return this.cacheService.getCached({
      path: 'status-list',
      source: () => {
        return this.http.get<StatusOccurrenceLike[]>(
          `~Ocorrencia/StatusOcorrencia`
        );
      },
    });
  }

  getOccurrenceTypeList() {
    return this.cacheService.getCached({
      path: 'type-list',
      source: () => {
        return this.http.get<OccurrenceTypeModelLike[]>(
          `~Ocorrencia/TiposOcorrencia`
        );
      },
    });
  }

  queryList(
    filter: Partial<SearchOccurrenceFilterLike>,
    take: number,
    skip: number
  ): Observable<PaginationResult<OccurrenceModel>> {
    let params = new HttpParams();
    params = params.append('take', take).append('skip', skip);

    if (filter) {
      const keys = Object.keys(filter) as Array<
        keyof SearchOccurrenceFilterLike
      >;
      for (const key of keys) {
        if (!filter[key]) {
          continue;
        }

        let field: string;
        switch (key) {
          case 'status':
            field = 'statusOcorrenciaId';
            break;
          case 'type':
            field = 'tipoOcorrenciaId';
            break;
          case 'entityId':
            field = 'idEntidade';
            break;
          case 'entityIdNotified':
            field = 'idEntidadeNotificada';
            break;
          case 'startDate':
            field = 'dataInicio';
            break;
          case 'endDate':
            field = 'dataFim';
            break;
          default:
            field = key;
            break;
        }

        let value = filter[key];
        if (value instanceof Date) {
          value = moment(value).format('MM-DD-YYYY');
        }

        params = params.append(field, value);
      }
    }

    return this.http
      .get<PaginationResult<OccurrenceDTOLike>>(`~Ocorrencia/Filter`, {
        params,
      })
      .pipe(
        map((res) => {
          const totalCount = res.totalCount;
          const data = res.data?.map((item) => this.apiToModel(item)) ?? [];
          return { totalCount, data };
        })
      );
  }

  listAttachmentsByOccurrenceId(
    occurrenceId: string
  ): Observable<OccurrenceAttachmentModelLike[]> {
    return this.http
      .get<{ anexos: any[] }>(`~Ocorrencia/FindAnexos`, {
        params: { id: occurrenceId },
      })
      .pipe(
        map((res) => {
          return this.attachmentsFromApi(res?.anexos);
        })
      );
  }

  getById(occurrenceId: string) {
    return this.http
      .get<OccurrenceDTOLike>(`~Ocorrencia/${occurrenceId}`)
      .pipe(this.apiToModelOperator());
  }

  create(model: OccurrenceModelLike) {
    const body = this.modelToApi(model);
    return this.http
      .post<OccurrenceDTOLike>(`~Ocorrencia`, body)
      .pipe(this.apiToModelOperator());
  }

  update(model: OccurrenceModelLike) {
    const body = this.modelToApi(model);
    return this.http
      .put<OccurrenceDTOLike>(`~Ocorrencia`, body)
      .pipe(this.apiToModelOperator());
  }

  setStatusSiia(
    occurrenceId: string,
    statusSiia: StatusOccurrenceType,
    descriptionCanceled?: string
  ) {
    return this.getOccurrenceStatusList().pipe(
      map((list) => {
        return list?.find((x) => x?.idSiia === statusSiia)?.id;
      }),
      mergeMap((status) => {
        const body = {
          idOcorrencia: occurrenceId,
          statusOcorrenciaId: status,
          observacaoCancelada: descriptionCanceled,
        };

        return this.http
          .put<OccurrenceDTOLike>(`~Ocorrencia/Status`, body)
          .pipe(this.apiToModelOperator());
      })
    );
  }

  private apiToModelOperator() {
    return map((e) => this.apiToModel(e as any));
  }

  private modelToApi(model: OccurrenceModelLike): OccurrenceDTOLike {
    const modelApi: OccurrenceDTOLike = {
      statusOcorrenciaId: model.status,
      tipoOcorrenciaId: model.type,
      observacoes: model.observation,
      geometry: model.geometry,
      notas:
        model.notes?.map((x) => {
          return {
            id: x.id,
            nota: x.text,
          };
        }) ?? [],
      anexos:
        model.attachments?.map((x) => {
          return {
            id: x.id,
            nome: x.text,
            url: x.url,
          };
        }) ?? [],
      objetos:
        model.objects?.map((x) => {
          return {
            id: x.id,
            tipoObjeto: x.type,
            idEntidade: x.entityId,
            idObjetoCadastro: x.objectRegisterId,
          };
        }) ?? [],
    };

    if (model?.id) {
      modelApi.id = model.id;
    }

    return modelApi;
  }

  private apiToModel(apiModel: OccurrenceDTOLike): OccurrenceModel {
    const model: OccurrenceModelLike = {
      id: apiModel.id,
      userId: apiModel.idUtilizadorCriacao,
      entityId: apiModel.idEntidadeResponsavel,
      notifiedEntityList:
        apiModel.entidadesNotificadas?.map((x) => {
          return {
            id: x.idEntidade,
            status: x.estado,
          };
        }) ?? [],
      createdAt: apiModel.dataOcorrencia,
      geometry: apiModel.geometry,
      idsic: apiModel.idsic,
      observation: apiModel.observacoes,
      status: apiModel.statusOcorrenciaId ?? apiModel.statusOcorrencia?.id,
      statusSiia: apiModel.statusOcorrencia?.idSiia as any,
      type: apiModel.tipoOcorrenciaId ?? apiModel.tipoOcorrencia?.id,
      typeSiia: apiModel.tipoOcorrencia?.idSiia,
      notes:
        apiModel.notas?.map((x) => {
          return {
            id: x.id,
            text: x.nota,
          };
        }) ?? [],
      attachments: this.attachmentsFromApi(apiModel.anexos),
      objects:
        apiModel.objetos?.map((x) => {
          return {
            id: x.id,
            type: x.tipoObjeto,
            entityId: x.idEntidade,
            objectRegisterId: x.idObjetoCadastro,
          };
        }) ?? [],
    };

    return new OccurrenceModel(model);
  }

  private attachmentsFromApi(
    attachments: OccurrenceDTOLike['anexos']
  ): OccurrenceAttachmentModelLike[] {
    return (
      attachments?.map((x) => {
        return {
          id: x.id,
          text: x.nome,
          url: x.url,
        };
      }) ?? []
    );
  }
}
